import {TitleCasePipe} from '@angular/common';
import {Component, OnDestroy, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Breadcrumb} from '@app/models/breadcrumb.model';
import { CompanyFeatures } from '@app/models/company-features.model';
import {UserRole, RoleName} from '@app/models/user-role.model';
import {FilterMethod, FilterOption} from '@app/models/universal-filter-option.model';
import {IHelpVideo} from '@app/models/video-library/help-video.model';
import {VideoLibraryAPIService} from '@app/shared/api/video-library.api.service';
import {BreadcrumbService} from '@app/shared/breadcrumbs/breadcrumbs.service';
import {Globals} from '@app/shared/globals/globals';
import {PaginationNewComponent} from '@app/shared/pagination/pagination-new/pagination-new.component';
import {UnderscoreToSpacePipe} from '@app/shared/pipes/underscore_to_space.pipe';
import {Categories, Category, DefaultFilter} from '@app/shared/universal-filter/universal-filter.component';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

interface IUniversalFilterData {
  searchControl: FormControl;
  filterOptions: FilterOption[];
  searchProps: string[];
  lastFilterResult: number[];
  defaultFilters: DefaultFilter;
  currentCategories: Categories;
}

interface PageState {
  loading: boolean;
  error: boolean;
  errorMessage: string;
}

@Component({
  selector: 'app-video-library-browse',
  templateUrl: './video-library-browse.component.html',
  styleUrls: ['./video-library-browse.component.scss']
})
export class VideoLibraryBrowseComponent implements OnDestroy {
  private readonly ngUnsubscribe$ = new Subject<void>();

  @ViewChild('pagination') pagination?: PaginationNewComponent;

  videoListMaster: IHelpVideo[];
  videoListFiltered: IHelpVideo[];
  videoListDisplay: IHelpVideo[];

  state: PageState;

  breadcrumb: Breadcrumb;

  universalFilterData: IUniversalFilterData;

  constructor(
    public globals: Globals,
    public route: ActivatedRoute,
    private router: Router,
    private videoLibraryAPIService: VideoLibraryAPIService,
    private breadcrumbService: BreadcrumbService,
    private titlecasePipe: TitleCasePipe,
    private underscoreToSpacePipe: UnderscoreToSpacePipe,
  ) {
    this.videoListMaster = [];
    this.videoListFiltered = [];
    this.videoListDisplay = [];

    this.state = {
      loading: true,
      error: false,
      errorMessage: ''
    };

    this.universalFilterData = this.initUniversalFilter();

    this.breadcrumb = this.breadcrumbService.init(this.route, true);

    this.router.events
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(event => {
        if (event instanceof NavigationEnd && this.route.children.length === 0) {
          this.getData();
        }
      });
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.unsubscribe();
  }

  getData(): void {
    this.state.loading = true;

    this.videoLibraryAPIService.getAllVideos()
      .subscribe(
        (videos) => {
          this.videoListMaster = videos;
          this.videoListFiltered = videos;
          this.populateUniversalFilter(videos);
          this.state.loading = false;
        }
      );
  }

  // #region - UNIVERSAL FILTER
  initUniversalFilter(): IUniversalFilterData {
    return {
      searchControl: new FormControl('', []),
      filterOptions: [],
      searchProps: [],
      lastFilterResult: [],
      defaultFilters: {},
      currentCategories: {}
    };
  }

  populateUniversalFilter(data: IHelpVideo[]) {
    this.universalFilterData.searchProps = ['Title'];
    this.universalFilterData.filterOptions = data.map(v => this.getFilterOptionsForPerson(v));
    const categoriesSelected = (this.universalFilterData.currentCategories && (Object.keys(this.universalFilterData.currentCategories).length > 0));
    this.universalFilterData.defaultFilters = ((categoriesSelected) ? this.getSelectedFilters() : this.getDefaultFilters());
  }

  getFilterOptionsForPerson(video: IHelpVideo): FilterOption {
    const option: FilterOption = {
      id: video.id,
      properties: {
        'Title': { value: video.title, filterMethod: FilterMethod.OR },
        'Feature': { value: this.getFilterFeature(video.features), filterMethod: FilterMethod.OR },
        'Watched': { value: this.getFilterWatched(video.watched), filterMethod: FilterMethod.OR },
        'Access level': { value: this.getFilterAccessLevel(video.accessLevel), filterMethod: FilterMethod.OR }
      }
    };

    return option;
  }


  getFilterFeature(features: CompanyFeatures[]): string | string[] {
    if (!features || (features.length === 0)) {
      return 'No feature';
    }

    return features.map(f => this.titlecasePipe.transform(this.underscoreToSpacePipe.transform(f)));
  }

  getFilterWatched(watched: boolean): string {
    return (watched ? 'Watched' : 'Not watched');
  }

  getFilterAccessLevel(accessLevel: UserRole | null): string | undefined {
    if (!accessLevel) {
      return 'Everyone';
    }

    if (accessLevel.name === RoleName.USER) {
      return 'Employee';
    }

    return this.titlecasePipe.transform(accessLevel.name);
  }

  getSelectedFilters(): DefaultFilter {
    const data = this.universalFilterData.currentCategories;
    const output: DefaultFilter = {};

    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        const keyCategories: Category[] = data[key];
        const categoriesEnabled = keyCategories.filter(c => c.enabled).map(c => c.name.toLowerCase());

        if (!output[key]) {
          output[key] = [];
        }

        output[key] = [...output[key], ...categoriesEnabled];
      }
    }

    return output;
  }

  getDefaultFilters(): DefaultFilter {
    const defaultFilters: DefaultFilter = {};

    defaultFilters['Feature'] = this.getDefaultFiltersFeature();
    defaultFilters['Access level'] = this.getDefaultFiltersAccessLevel();
    defaultFilters['Watched'] = ['Not watched'];

    return defaultFilters;
  }

  getDefaultFiltersFeature(): string[] {
    const featuresEnabled = this.globals.company.features
      .filter(f => f.enabled)
      .map(f => this.titlecasePipe.transform(this.underscoreToSpacePipe.transform(f.feature)));

    return ['no feature', ...featuresEnabled];
  }

  getDefaultFiltersAccessLevel(): string[] {
    let accessLevelsEnabled: string[] = [ 'Everyone', 'Employee' ];

    // Admins can see admin, manager and user videos
    if (this.globals.hasRole(RoleName.ADMIN) || this.globals.hasRole(RoleName.FRANKLI_ADMIN)) {
      accessLevelsEnabled = [...accessLevelsEnabled, RoleName.ADMIN];
      accessLevelsEnabled = [...accessLevelsEnabled, RoleName.MANAGER];
    }

    // Managers can see manager and user videos
    if (this.globals.hasRole(RoleName.MANAGER) || this.globals.hasRole(RoleName.SECONDARY_MANAGER)) {
      accessLevelsEnabled = [...accessLevelsEnabled, RoleName.MANAGER];
    }

    return accessLevelsEnabled;
  }

  filterEmit(results: number[], doRefresh = true): void {
    this.universalFilterData.lastFilterResult = results;
    this.videoListFiltered = this.videoListMaster.filter(v => results.includes(v.id));

    if (doRefresh) {
      this.refreshPagination();
    }
  }

  filterCategoriesChanged(categories: Categories): void {
    this.universalFilterData.currentCategories = categories;
  }
  // #endregion

  refreshPagination(): void {
    if (this.pagination) {
      this.pagination.update();
    }
  }
}
